import React, { createContext, useState, useContext } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { baseSchema, formatPhoneForSubmit } from "./utils";
import { z } from "zod";
import api from "../../../../../../../service";
import { useDispatch, useSelector } from "react-redux";
import generateHeaders from "../../../../../../../utils/generateHeaders";
import getEcommerceErrorDescription from "../../../../../../../utils/getEcommerceErrorDescription";
import {
  purchaseInfo,
  resetSaleInfo,
} from "../../../../../../../redux/modules/staff-acess/actions";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import useNotification from "../../../../../../../hooks/newUXNotification";
import { useIntl } from "react-intl";
import { DEFAULT_DDI_COUNTRY_KEY } from "../../../../../../../utils/declarationConstants";

const cardSchema = baseSchema.extend({
  cardReader: z
    .string({ message: "error.field_required" })
    .min(1, { message: "error.field_required" }),
  preference: z.enum(["credit", "debit"], {
    required_error: "select_option",
  }),
  brand: z
    .string({ message: "select_option" })
    .min(1, { message: "select_option" }),
});
const pixSchema = baseSchema.extend({
  cardReader: z
    .string({ message: "error.field_required" })
    .min(1, { message: "error.field_required" }),
});
const cashSchema = baseSchema;

const FormContext = createContext(null);

export const FormProvider = ({ children }) => {
  const [selectedPaymentMethod, setSelectPaymentMethod] = useState("");
  const [selectedPaymentMethodError, setSelectedPaymentMethodError] =
    useState(false);
  const { accountId, orderNumber } = useSelector((state) => state.staff_access);
  const { user, token } = useSelector((state) => state.auth);
  const headers = generateHeaders(token);
  const [loading, setLoading] = useState();
  const [cancelling, setCancelling] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const intl = useIntl();
  const notification = useNotification();

  const cardForm = useForm({
    resolver: zodResolver(cardSchema),
    mode: "onBlur",
    defaultValues: { ddi: DEFAULT_DDI_COUNTRY_KEY },
  });

  const pixForm = useForm({
    resolver: zodResolver(pixSchema),
    mode: "onBlur",
    defaultValues: { ddi: DEFAULT_DDI_COUNTRY_KEY },
  });

  const cashForm = useForm({
    resolver: zodResolver(cashSchema),
    mode: "onBlur",
    defaultValues: { ddi: DEFAULT_DDI_COUNTRY_KEY },
  });

  const submitActiveForm = () => {
    switch (selectedPaymentMethod) {
      case "card":
        cardForm.handleSubmit(cardSubmit)();
        break;
      case "pix":
        pixForm.handleSubmit(pixSubmit)();
        break;
      case "cash":
        cashForm.handleSubmit(cashSubmit)();
        break;
      default:
        return () => {};
    }
  };

  const generateBody = (objectData) => {
    const body = {
      CONFIRMACAOPEDIDO: {
        PEDIDO: {
          numpedido: orderNumber,
          status: 1,
          Empresa: accountId,
          NomeAtendente: user.name,
          ...objectData,
        },
      },
    };

    return JSON.stringify(body);
  };

  const getCardBrandName = (cardBrand) => {
    if (cardBrand === "American Express") {
      return "Amex";
    }

    return cardBrand;
  };

  const cardSubmit = async (data) => {

    try {
      setLoading(true);

      await api.post(
        "/admin/ecommerce/send-order-confirmation",
        generateBody({
          Formapagamento: data.preference === "credit" ? 11 : 10,
          CPF: data.document,
          Telefone: formatPhoneForSubmit(data.ddi, data.phone),
          NomeComprador: data.name,
          Maquininha: data.cardReader,
          Bandeira: getCardBrandName(data.brand),
        }),
        headers
      );

      dispatch(
        purchaseInfo({
          data: {
            ...data,
            phone: formatPhoneForSubmit(data.ddi, data.phone),
            paymentMethod: data.preference === "credit" ? "credit" : "debit",
          },
        })
      );
      history.push("/staff-access/services/sales/purchase-summary");
      setLoading(false);
    } catch (error) {
      notification.displayError(
        getEcommerceErrorDescription(intl, error, "api.error.-18027"),
        "financial-Single-SignOn-info"
      );
      setLoading(false);
    }
  };

  const pixSubmit = async (data) => {
    try {
      setLoading(true);

      await api.post(
        "/admin/ecommerce/send-order-confirmation",
        generateBody({
          Formapagamento: 8,
          CPF: data.document,
          Telefone: formatPhoneForSubmit(data.ddi, data.phone),
          NomeComprador: data.name,
          Maquininha: data.cardReader,
        }),
        headers
      );

      dispatch(
        purchaseInfo({
          data: {
            ...data,
            phone: formatPhoneForSubmit(data.ddi, data.phone),
            paymentMethod: "pix",
          },
        })
      );
      history.push("/staff-access/services/sales/purchase-summary");
      setLoading(false);
    } catch (error) {
      notification.displayError(
        getEcommerceErrorDescription(intl, error, "api.error.-18027"),
        "financial-Single-SignOn-info"
      );
      setLoading(false);
    }
  };

  const cashSubmit = async (data) => {
    try {
      setLoading(true);

      await api.post(
        "/admin/ecommerce/send-order-confirmation",
        generateBody({
          Formapagamento: 5,
          CPF: data.document,
          Telefone: formatPhoneForSubmit(data.ddi, data.phone),
          NomeComprador: data.name,
        }),
        headers
      );

      dispatch(
        purchaseInfo({
          data: {
            ...data,
            phone: formatPhoneForSubmit(data.ddi, data.phone),
            paymentMethod: "cash",
          },
        })
      );
      history.push("/staff-access/services/sales/purchase-summary");
      setLoading(false);
    } catch (error) {
      notification.displayError(
        getEcommerceErrorDescription(intl, error, "api.error.-18027"),
        "financial-Single-SignOn-info"
      );
      setLoading(false);
    }
  };

  // Cancelamento de pedido nao confirmado (jornada Staff). O Chleba nao expoe
  // um metodo dedicado de cancelamento: o backend reaproveita o ConfirmaPedido
  // com status=2 (Cancelado) por baixo dos panos. Daqui so precisa sair o
  // numero do pedido (OrderCancellationRequest, backend).
  const generateCancelBody = () =>
    JSON.stringify({
      CANCELAMENTOPEDIDO: {
        PEDIDO: {
          numpedido: orderNumber,
        },
      },
    });

  const cancelOrder = async () => {
    try {
      setCancelling(true);

      await api.post(
        "/admin/ecommerce/send-order-cancellation",
        generateCancelBody(),
        headers
      );

      notification.displaySuccess(
        intl.formatMessage({ id: "order_canceled_success" }),
        "order-cancellation-success"
      );
      // Pedido cancelado: limpa carrinho, checkout e o estado persistido
      // antes de retornar para a tela de venda.
      dispatch(resetSaleInfo());
      history.push("/staff-access/services/sales");
    } catch (error) {
      notification.displayError(
        getEcommerceErrorDescription(intl, error, "api.error.-18029"),
        "order-cancellation-error"
      );
    } finally {
      setCancelling(false);
    }
  };

  return (
    <FormContext.Provider
      value={{
        selectedPaymentMethod,
        setSelectPaymentMethod,
        selectedPaymentMethodError,
        setSelectedPaymentMethodError,
        cardForm,
        pixForm,
        cashForm,
        submitActiveForm,
        submitting: loading,
        cancelOrder,
        cancelling,
      }}
    >
      {children}
    </FormContext.Provider>
  );
};

export const useFormContext = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error("useFormContext must be used within a FormProvider");
  }
  return context;
};
